

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React from 'react';
import { formatDigits } from '../../Utils/formatNumbers';
import { dateStringTimeConvert } from '../../Utils/titleCaseFunction';
import { useCompany } from '../../Context/company-context';

// Container copied from A4Receipt for consistent layout
const container = css`
  width: 100%;
  margin: auto;
  padding: 15px;
  font-family: Arial, sans-serif;
  color: #333;
  background-color: #fff;
  box-sizing: border-box;

  @media print {
    @page {
      size: A4 portrait;
      margin: 15mm 15mm 15mm 15mm;
    }
    width: 100%;
    min-height: 297mm;
    padding: 5mm;
    -webkit-print-color-adjust: exact;
  }

  h1 {
    font-size: 16pt;
  }

  h2 {
    font-size: 15pt;
  }

  td, th {
    font-size: 12pt;
  }
`;

// Header styling copied from A4Receipt
const header = css`
  text-align: center;
  margin-bottom: 20px;

  img {
    display: block;
    margin: 0 auto 10px;
    max-width: 150px;
  }

  h1 {
    margin: 0;
    font-size: 16pt;
    font-weight: bold;
  }

  h2 {
    margin: 5px 0 15px;
    font-size: 15pt;
    font-weight: bold;
  }

  p {
    margin: 2px 0;
    font-size: 12pt;
  }
`;

// Customer info table copied from A4Receipt
const customerInfo = css`
  width: 100%;
  margin-bottom: 20px;
  border-collapse: collapse;
  font-size: 14pt;

  td {
    padding: 4px 6px;
    vertical-align: top;
    color: #333;
    font-size: 14pt;

  }

  strong {
    color: #555;
  }

  tr + tr td {
    padding-top: 6px;
  }
`;

// Table styling copied from A4Receipt
const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;

  th,
  td {
    border: 1px solid #ccc;
    padding: 3px;
    font-size: 15pt;
    color: #000;
  }

  th {
    background-color: #f2f2f2;
    font-weight: bold;
    text-align: left;
    color: #000;
    font-size: 16pt;
  }

  td {
    text-align: left;
  }

  td.amount,
  td.unitPrice {
    text-align: right;
  }
`;

// Totals styling copied from A4Receipt
const totals = css`
  width: 100%;
  border-collapse: collapse;
  margin-top: 5px;
  font-size: 18pt;

  td {
    padding: 8px;
    color: #000;
    font-weight: bold;
    font-size: 14pt;

  }

  td.right {
    text-align: right;
  }
`;

// Payments styling adapted for this layout
const payments = css`
  margin-top: 6mm;
  font-size: 15pt;
  border: 1px solid #ccc;
  padding: 4mm;
  background-color: #fafafa;

  p {
    margin: 2mm 0;
  }
`;

// Footer styling copied from A4Receipt
const footer = css`
  margin-top: 40px;
  font-size: 12pt;
  text-align: center;
  border-top: 1px solid #ccc;
  padding-top: 10px;
`;

const InvoiceContent = React.forwardRef(({ company, customer, products }, ref) => {
  // Safely extract customer details
  const customerName =
    customer.customer_name ||
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

  const invoiceDate = customer.date ? dateStringTimeConvert(customer.date) : '';
  const invoiceNumber = customer.invoice_number || 'N/A';
  const totalAmount = customer.total_amount || 0;
  const totalDiscount = customer.total_discount || 0;

    const { state:{ companyName, companyEmail, mobile, address, website, logo } } = useCompany();

  return (
    <div css={container} ref={ref} id="customA4Print">
      {/* Company Header */}
      <div css={header}>
        {logo && (
          <img src={logo} alt={companyName} />
        )}
        <h1>{companyName || "VIP PHARMACY"}</h1>
        <p>{address || "Address not available"}</p>
        <p>Phone: {mobile || "N/A"}</p>
        <p>Email: {companyEmail || "N/A"}</p>
      </div>

      {/* Customer Info */}
      <table css={customerInfo}>
        <tbody>
          <tr>
            <td>
              <strong>Invoice To:</strong> {customerName} <br />
              <strong>Contact:</strong> {customer.customer_mobile || "N/A"} <br />
              <strong>Address:</strong> {customer.customer_address || "N/A"}
            </td>
            <td>
              <strong>Invoice #:</strong> {invoiceNumber} <br />
              <strong>Date:</strong> {invoiceDate}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Product Table */}
      <table css={tableStyle}>
        <thead>
          <tr>
            <th>No.</th>
            <th>Brand Name</th>
            <th>Batch</th>
            <th>Expiry</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            // Ensure safe numeric conversion
            const qty = p.quantity ? +p.quantity : 0;
            const rate = p.rate ? +p.rate : 0;
            const discount = p.discount_amount ? +p.discount_amount : 0;

            // Calculate discounted rate and total
            const finalRate = rate - discount;
            const total = finalRate * qty;

            return (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{p.product_name}</td>
                <td>{p.batch_id}</td>
                <td>{p.expire_date}</td>
                <td>{qty}</td>
                {/* Show discounted rate */}
                <td className="unitPrice">{formatDigits(finalRate)}</td>
                {/* Show total amount */}
                <td className="amount">{formatDigits(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals Section */}
      <table css={totals}>
        <tbody>
          {/* <tr>
            <td>Total Discount:</td>
            <td className="right">{formatDigits(totalDiscount)}</td>
          </tr> */}
          <tr>
            <td>Total Amount:</td>
            <td className="right">{formatDigits(totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      {/* Payments Info */}
      <div css={payments}>
        <p><strong>ABSA Bank:</strong> 6008659379 - VIP PHARMACY</p>
        <p><strong>Mobile Money:</strong></p>
        <p>MTN: Merchant ID: 731614</p>
        <p>Airtel: Merchant ID: 6682216</p>
      </div>

      {/* Footer Section */}
      <div css={footer}>
        <p>Thank you for your business!</p>
        <p>Created by: {customer.dispenser || "System"}</p>
        {/* <p>     Merry Christmas & Happy Holidays!    </p> */}
      </div>
    </div>
  );
});

export default InvoiceContent;
